import { useState, useEffect, useRef, useCallback, lazy, Suspense } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { oneDark } from "@codemirror/theme-one-dark";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { LivePodium } from "../components/LivePodium";
import { LobbyMusic } from "../components/LobbyMusic";
import { LiveLeaderboard } from "../components/LiveLeaderboard";
import { fmtSecs } from "../lib/liveTime";
import { clientHeaders } from "../lib/clientId";
import { TrackBadge } from "../components/TrackBadge";
import { QuestionCountdown } from "../components/live/QuestionCountdown";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
const BlocksSolutionView = lazy(() => import("../components/blocks/BlocksSolutionView").then((m) => ({ default: m.BlocksSolutionView })));
const BlocksWorkspace = lazy(() => import("../components/blocks/BlocksWorkspace").then((m) => ({ default: m.BlocksWorkspace })));
const NavWorkspace = lazy(() => import("../components/nav/NavWorkspace").then((m) => ({ default: m.NavWorkspace })));
const NavReplay = lazy(() => import("../components/nav/NavReplay").then((m) => ({ default: m.NavReplay })));
import {
  MonitorPlay, Users, Loader2, Star, Play, Trophy, XCircle, AlertTriangle,
  CheckCircle2, Clock, Radio, StopCircle, ArrowRight, WifiOff, Copy, Terminal, Code2, Eye, EyeOff,
  Lock, Unlock, UserX, RotateCcw,
} from "lucide-react";
import { isBlockMode, normalizeMode, MODES, MODE_META } from "../lib/modes";

const API = "/api";
const DEFAULT_DEMO_CODE = "# Demonstrate your solution here\n";

const STATUS_STYLES = {
  waiting: { label: "Waiting", cls: "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300" },
  working: { label: "Working", cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300" },
  correct: { label: "Solved", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" },
  incorrect: { label: "Trying", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" },
};

export default function LiveHost({ user }) {
  const navigate = useNavigate();
  const [drills, setDrills] = useState([]);
  const [track, setTrack] = useState("python");
  const [loadingDrills, setLoadingDrills] = useState(false);
  const [creating, setCreating] = useState(false);
  const [timeLimit, setTimeLimit] = useState(5); // minutes per question (1-15)
  const [session, setSession] = useState(null);
  const [state, setState] = useState(null);
  const [ended, setEnded] = useState(null);
  const [connected, setConnected] = useState(false);
  const [checkedActive, setCheckedActive] = useState(false);
  const [demoCode, setDemoCode] = useState(DEFAULT_DEMO_CODE);
  const [demoInput, setDemoInput] = useState("");
  const [demoOutput, setDemoOutput] = useState("");
  const [demoRunning, setDemoRunning] = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const [sampleSolution, setSampleSolution] = useState(null);
  const [blocksSolution, setBlocksSolution] = useState(null);
  const [navSolution, setNavSolution] = useState(null);  // Junior Blocks: { nav_grid, nav_run } for the replay
  const [continueOpen, setContinueOpen] = useState(false);
  const [continueTrack, setContinueTrack] = useState(null); // null = follow the session's mode
  const [continuing, setContinuing] = useState(false);
  const wsRef = useRef(null);
  const closedRef = useRef(false);
  const demoQidRef = useRef(null);
  const isDarkMode = document.documentElement.classList.contains("dark");

  useEffect(() => {
    if (!user?.school_admin) return;
    const init = async () => {
      try {
        const res = await fetch(`${API}/live/sessions/active`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          if (data.session) {
            setSession(data.session);
          } else {
            fetchDrills();
          }
        }
      } catch { /* ignore */ }
      setCheckedActive(true);
    };
    init();
  }, [user]);

  const fetchDrills = async () => {
    setLoadingDrills(true);
    try {
      const res = await fetch(`${API}/school/drills`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        const list = (data.drills || []).filter((d) => d.question_count > 0);
        setDrills(list);
        const preferred = normalizeMode(user?.mode);
        setTrack(list.some((d) => normalizeMode(d.mode) === preferred) ? preferred : (MODES.find((m) => list.some((d) => normalizeMode(d.mode) === m)) || preferred));
      }
    } catch { /* ignore */ }
    setLoadingDrills(false);
  };

  const trackCounts = Object.fromEntries(MODES.map((m) => [m, drills.filter((d) => normalizeMode(d.mode) === m).length]));
  const visibleDrills = drills
    .filter((d) => normalizeMode(d.mode) === track)
    .sort((a, b) => (a.difficulty || 0) - (b.difficulty || 0) || (a.title || "").localeCompare(b.title || ""));

  const connect = useCallback((sess) => {
    if (closedRef.current) return;
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const ws = new WebSocket(`${proto}://${window.location.host}/api/live/ws/${sess.session_id}?role=host`);
    wsRef.current = ws;
    const handshakeTimer = setTimeout(() => { if (ws.readyState === WebSocket.CONNECTING) ws.close(); }, 8000);

    ws.onopen = () => { clearTimeout(handshakeTimer); setConnected(true); };
    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === "session_state") {
        setState((prev) => (prev && msg.state_version < prev.state_version ? prev : msg));
      } else if (msg.type === "session_ended") {
        setEnded(msg);
        closedRef.current = true;
      } else if (msg.type === "idle_warning") {
        toast.warning(msg.message, { duration: 10000 });
      } else if (msg.type === "time_up") {
        toast.info("Time's up! Moving on…");
      } else if (msg.type === "error") {
        toast.error(msg.message);
      }
    };
    ws.onclose = (e) => {
      clearTimeout(handshakeTimer);
      setConnected(false);
      if (!closedRef.current && e.code !== 4404 && e.code !== 4403) {
        setTimeout(() => connect(sess), 2000);
      } else if (e.code === 4404 && !closedRef.current) {
        setEnded({ reason: "not_found", leaderboard: [] });
        closedRef.current = true;
      }
    };
  }, []);

  useEffect(() => {
    if (!session) return;
    closedRef.current = false;
    connect(session);
    const ping = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ action: "ping" }));
      }
    }, 25000);
    return () => {
      closedRef.current = true;
      clearInterval(ping);
      wsRef.current?.close();
    };
  }, [session, connect]);

  // Reset the teacher's demo editor when a new question starts
  useEffect(() => {
    const qid = state?.question?.question_id;
    if (state?.status === "question_active" && qid && qid !== demoQidRef.current) {
      demoQidRef.current = qid;
      setDemoCode(DEFAULT_DEMO_CODE);
      setDemoInput(state.question.example_input || "");
      setDemoOutput("");
      setShowSolution(false);
      setSampleSolution(null);
      setBlocksSolution(null);
      setNavSolution(null);
    }
  }, [state]);

  // Each phase (question -> leaderboard -> next question) starts at the top of the page
  useEffect(() => {
    if (state?.status) window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [state?.status, state?.current_index]);

  const isBlocksQ = isBlockMode(state?.question?.mode);
  const isNavQ = !!state?.question && (state.question.mode === "junior" || state.question.blocks?.kind === "nav");
  // Blocks rooms: warm up the Blockly chunks so the leaderboard showcase doesn't flash "Loading blocks…"
  useEffect(() => {
    if (isBlockMode(state?.mode)) {
      import("../components/blocks/BlocksSolutionView");
      import("../components/blocks/BlocksWorkspace");
    }
    if (state?.mode === "junior") {
      import("../components/nav/NavWorkspace");
      import("../components/nav/NavReplay");
    }
  }, [state?.mode]);

  const handleDemoRun = async () => {
    setDemoRunning(true);
    setDemoOutput("");
    try {
      const res = await fetch(`${API}/code/test`, {
        method: "POST",
        headers: clientHeaders(),
        body: JSON.stringify({ code: demoCode, custom_input: demoInput, mode: isBlocksQ ? state.question.mode : "python" }),
      });
      const result = await res.json();
      if (result.success) {
        setDemoOutput(result.output?.trim() || "(No output)");
      } else {
        setDemoOutput(`Error: ${result.error || "Unknown error"}`);
      }
    } catch (e) {
      setDemoOutput(`Error: ${e.message}`);
    } finally {
      setDemoRunning(false);
    }
  };

  const toggleSolution = async () => {
    if (showSolution) {
      setShowSolution(false);
      return;
    }
    if (sampleSolution === null) {
      try {
        const res = await fetch(`${API}/live/sessions/${session.session_id}/solution`, { credentials: "include" });
        const data = await res.json();
        setSampleSolution(res.ok ? (data.sample_solution || "") : "");
        setBlocksSolution(res.ok ? data.blocks_solution || null : null);
        setNavSolution(res.ok && data.nav_grid ? { nav_grid: data.nav_grid, nav_run: data.nav_run } : null);
      } catch {
        setSampleSolution("");
        setBlocksSolution(null);
        setNavSolution(null);
      }
    }
    setShowSolution(true);
  };

  const continueWithDrill = async (drillId) => {
    setContinuing(true);
    try {
      const res = await fetch(`${API}/live/sessions/${session.session_id}/continue`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ drill_id: drillId, time_limit_minutes: timeLimit }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Added ${data.added_questions} questions - keep going!`);
        setContinueOpen(false);
      } else {
        toast.error(data.detail || "Could not continue with this drill");
      }
    } catch {
      toast.error("Could not continue with this drill");
    } finally {
      setContinuing(false);
    }
  };

  const sendAction = (action, payload = {}) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ action, ...payload }));
    } else {
      toast.error("Not connected - please wait a moment and try again");
    }
  };

  const toggleLock = () => {
    const locked = !state?.locked;
    sendAction("set_locked", { locked });
    toast(locked ? "Room locked - no new players can join" : "Room unlocked - players can join again");
  };

  const kickParticipant = (p) => {
    sendAction("kick_participant", { participant_id: p.participant_id });
    toast(`Removed ${p.nickname}`, {
      action: { label: "Undo", onClick: () => sendAction("unkick_participant", { participant_id: p.participant_id }) },
    });
  };

  const createSession = async (drillId) => {
    setCreating(true);
    try {
      const res = await fetch(`${API}/live/sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ drill_id: drillId, time_limit_minutes: timeLimit }),
      });
      const data = await res.json();
      if (res.ok) {
        setSession(data.session);
        toast.success("Live session created!");
      } else {
        toast.error(data.detail || "Could not create session");
      }
    } catch {
      toast.error("Could not create session");
    } finally {
      setCreating(false);
    }
  };

  const endSession = async () => {
    if (!window.confirm("End this live session for everyone?")) return;
    sendAction("end_session");
    try {
      await fetch(`${API}/live/sessions/${session.session_id}/end`, {
        method: "POST",
        credentials: "include",
      });
    } catch { /* WS already handled it */ }
  };

  const resetToNewSession = () => {
    setSession(null);
    setState(null);
    setEnded(null);
    closedRef.current = true;
    wsRef.current?.close();
    fetchDrills();
  };

  const copyCode = () => {
    navigator.clipboard.writeText(state?.join_code || session?.join_code || "");
    toast.success("Code copied!");
  };

  if (!user) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user.school_admin) {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="live-host-denied">
        <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <h1 className="font-heading font-bold text-2xl mb-2">School Admin Only</h1>
        <p className="text-muted-foreground text-sm">Only school admins can host live drills. Looking to join one instead?</p>
        <Button className="mt-5 gap-2" onClick={() => navigate("/join")} data-testid="live-host-goto-join-btn">
          <Radio className="w-4 h-4" /> Join a Live Drill
        </Button>
      </div>
    );
  }

  const joinUrl = `${window.location.origin}/join?code=${state?.join_code || session?.join_code || ""}`;
  const isLastQuestion = state && state.current_index >= state.total_questions - 1;

  const ParticipantGrid = () => (
    <div className="bg-card rounded-2xl border border-border p-5" data-testid="live-host-participants">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" /> Players
        </h3>
        <Badge variant="outline" className="text-base px-3 py-1" data-testid="live-host-players-count">
          {state?.participants?.length || 0} / {state?.max_participants}
        </Badge>
      </div>
      {(!state?.participants || state.participants.length === 0) ? (
        <p className="text-sm text-muted-foreground py-3 text-center">Waiting for players to join...</p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {state.participants.map((p) => {
            const style = STATUS_STYLES[p.status] || STATUS_STYLES.waiting;
            return (
              <div key={p.participant_id} className={`group pl-4 pr-2 py-2 rounded-full text-base sm:text-lg font-semibold flex items-center gap-2 ${style.cls}`} data-testid={`live-host-participant-${p.participant_id}`}>
                {p.status === "correct" && <CheckCircle2 className="w-5 h-5" />}
                {p.status === "working" && <Clock className="w-5 h-5" />}
                {p.status === "incorrect" && <XCircle className="w-5 h-5" />}
                {p.nickname}
                <span className="opacity-70 text-sm font-medium">
                  · {style.label}
                  {p.status === "correct" && p.time_taken != null && (
                    <span data-testid={`live-host-participant-time-${p.participant_id}`}> · {fmtSecs(p.time_taken)}</span>
                  )}
                  {" "}· {p.score}pts
                </span>
                <button
                  onClick={() => kickParticipant(p)}
                  className="ml-1 p-1 rounded-full opacity-50 hover:opacity-100 hover:bg-red-500 hover:text-white transition-colors"
                  title={`Remove ${p.nickname}`}
                  aria-label={`Remove ${p.nickname}`}
                  data-testid={`live-host-kick-${p.participant_id}`}
                >
                  <UserX className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      {state?.kicked?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border" data-testid="live-host-kicked-list">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Removed ({state.kicked.length})</p>
          <div className="flex flex-wrap gap-2">
            {state.kicked.map((k) => (
              <div key={k.participant_id} className="pl-3 pr-1.5 py-1 rounded-full text-sm bg-muted text-muted-foreground line-through flex items-center gap-1.5" data-testid={`live-host-kicked-${k.participant_id}`}>
                {k.nickname}
                <button
                  onClick={() => sendAction("unkick_participant", { participant_id: k.participant_id })}
                  className="no-underline p-1 rounded-full hover:bg-primary hover:text-primary-foreground transition-colors"
                  title={`Restore ${k.nickname}`}
                  aria-label={`Restore ${k.nickname}`}
                  data-testid={`live-host-unkick-${k.participant_id}`}
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // ===== Session ended summary =====
  if (ended) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-10" data-testid="live-host-ended">
        <div className="text-center mb-4">
          <h1 className="font-heading font-bold text-3xl mb-1">Session Complete</h1>
          {ended.reason === "idle_timeout" && (
            <p className="text-sm text-amber-600">This session was closed automatically due to 15 minutes of inactivity.</p>
          )}
        </div>
        {ended.leaderboard?.length > 0 ? (
          <LivePodium entries={ended.leaderboard} />
        ) : (
          <p className="text-sm text-muted-foreground text-center py-6">No players joined this session.</p>
        )}
        <Button className="w-full mt-6 gap-2 rounded-xl" onClick={resetToNewSession} data-testid="live-host-new-session-btn">
          <MonitorPlay className="w-4 h-4" /> Start a New Live Drill
        </Button>
      </div>
    );
  }

  // ===== Drill picker (no active session) =====
  if (!session) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8" data-testid="live-host-picker">
        <div className="mb-6">
          <h1 className="font-heading font-bold text-3xl flex items-center gap-3 mb-1">
            <MonitorPlay className="w-8 h-8 text-primary" /> Host a Live Drill
          </h1>
          <p className="text-muted-foreground text-sm">Pick a drill to run live. Students join with a code or QR — no account needed. Blocks drills open a block-coding workspace for players; Junior Blocks drills put the treasure grid on everyone's screen.</p>
        </div>
        {(!checkedActive || loadingDrills) ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : drills.length === 0 ? (
          <p className="text-muted-foreground text-center py-12">No drills available yet.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-5" role="tablist" aria-label="Coding mode" data-testid="live-host-track-filter">
              {MODES.map((m) => {
                const meta = MODE_META[m];
                const Icon = meta.icon;
                const active = track === m;
                return (
                  <button
                    key={m}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTrack(m)}
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${active ? `${meta.pill} ${meta.border} shadow-sm` : "bg-card text-muted-foreground border-border hover:text-foreground hover:border-foreground/30"}`}
                    data-testid={`live-host-track-${m}`}
                  >
                    <Icon className="w-4 h-4" />
                    {meta.label}
                    <span className={`rounded-full px-1.5 py-0.5 text-[11px] leading-none ${active ? "bg-white/25" : "bg-muted"}`} data-testid={`live-host-track-count-${m}`}>{trackCounts[m]}</span>
                  </button>
                );
              })}
              <span className="ml-auto text-xs text-muted-foreground flex items-center gap-1" data-testid="live-host-sort-note"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> easiest first</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap mb-5 rounded-2xl border border-border bg-card px-4 py-3" data-testid="live-host-time-limit">
              <Clock className="w-4 h-4 text-primary shrink-0" />
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-semibold">Time per question</p>
                <p className="text-xs text-muted-foreground">When time runs out the leaderboard shows automatically and you move on. Max 15 minutes.</p>
              </div>
              <Select value={String(timeLimit)} onValueChange={(v) => setTimeLimit(Number(v))}>
                <SelectTrigger className="w-[130px]" data-testid="live-host-time-limit-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 15 }, (_, i) => i + 1).map((m) => (
                    <SelectItem key={m} value={String(m)} data-testid={`live-host-time-limit-${m}`}>{m} min{m > 1 ? "s" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {visibleDrills.length === 0 ? (
              <p className="text-muted-foreground text-center py-12" data-testid="live-host-track-empty">No {MODE_META[track].label} drills available yet.</p>
            ) : (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="live-host-drill-grid">
            {visibleDrills.map((drill) => (
              <button
                key={drill.drill_id}
                onClick={() => createSession(drill.drill_id)}
                disabled={creating}
                className="text-left p-5 rounded-2xl border border-border bg-card hover:border-primary/50 hover:shadow-md transition-all disabled:opacity-50"
                data-testid={`live-host-drill-${drill.drill_id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="font-semibold flex items-center gap-2 flex-wrap">{drill.title}<TrackBadge mode={drill.mode} testId={`live-host-drill-track-${drill.drill_id}`} /></h3>
                  <div className="flex shrink-0">
                    {[...Array(drill.difficulty || 0)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                </div>
                {drill.description && <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{drill.description}</p>}
                <Badge variant="outline">{drill.question_count} questions</Badge>
                {drill.ungradable_count > 0 && (
                  <div className="mt-2.5 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-2.5 py-2" data-testid={`live-host-drill-warning-${drill.drill_id}`}>
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    <span>
                      {drill.ungradable_count} question{drill.ungradable_count > 1 ? "s" : ""} can't be graded (no test data):{" "}
                      <strong>{(drill.ungradable_titles || []).join(", ")}</strong>. Add test cases or an example output before hosting, or players will get errors on submit.
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>
            )}
          </>
        )}
      </div>
    );
  }

  // ===== Active session =====
  return (
    <div className="max-w-7xl mx-auto px-4 py-6" data-testid="live-host-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${connected ? "bg-green-400" : "bg-red-400"}`} />
              <span className={`relative inline-flex rounded-full h-3 w-3 ${connected ? "bg-green-500" : "bg-red-500"}`} />
            </span>
            {state?.drill_title || session.drill_title}
            <TrackBadge mode={state?.mode || session.mode} testId="live-host-track-badge" />
          </h1>
          <p className="text-sm text-muted-foreground">
            Live Drill Session {!connected && <span className="text-red-500 inline-flex items-center gap-1 ml-1"><WifiOff className="w-3 h-3" /> reconnecting...</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <LobbyMusic active={state?.status === "lobby"} />
          {state && (
            <Button
              variant={state.locked ? "default" : "outline"}
              size="sm"
              className="gap-2"
              onClick={toggleLock}
              title={state.locked ? "Unlock room to allow new players" : "Lock room so no new players can join"}
              data-testid="live-host-lock-btn"
            >
              {state.locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
              {state.locked ? "Locked" : "Lock Room"}
            </Button>
          )}
          <Button variant="destructive" size="sm" className="gap-2" onClick={endSession} data-testid="live-host-end-btn">
            <StopCircle className="w-4 h-4" /> End Session
          </Button>
        </div>
      </div>

      {!state ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Lobby */}
          {state.status === "lobby" && (
            <div className="grid lg:grid-cols-2 gap-6" data-testid="live-host-lobby">
              <div className="bg-card rounded-2xl border border-border p-8 text-center">
                <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl leading-tight mb-6" data-testid="live-host-join-heading">
                  Visit <span className="text-primary [overflow-wrap:anywhere]">{window.location.host}/join</span> or scan the QR code
                </h2>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-2">Join Code</p>
                <button onClick={copyCode} className="group" data-testid="live-host-join-code" title="Click to copy">
                  <span className="font-mono font-bold text-5xl sm:text-6xl tracking-[0.15em] text-primary">
                    {state.join_code}
                  </span>
                  <Copy className="w-4 h-4 inline-block ml-2 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </button>
                <div className="flex justify-center my-6">
                  <div className="p-4 bg-white rounded-2xl border border-border" data-testid="live-host-qr">
                    <QRCodeSVG value={joinUrl} size={180} />
                  </div>
                </div>
                {state.question_time_limit && (
                  <p className="mt-4 text-sm text-muted-foreground inline-flex items-center gap-1.5" data-testid="live-host-lobby-time-limit">
                    <Clock className="w-4 h-4" /> {Math.round(state.question_time_limit / 60)} min per question
                  </p>
                )}
                {state.locked && (
                  <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-400" data-testid="live-host-locked-note">
                    <Lock className="w-3.5 h-3.5" /> Room locked - new players can't join
                  </div>
                )}
              </div>
              <div className="space-y-4">
                <ParticipantGrid />
                <Button
                  className="w-full h-14 rounded-xl text-base gap-2"
                  onClick={() => sendAction("next_question")}
                  disabled={!state.participants?.length}
                  data-testid="live-host-start-btn"
                >
                  <Play className="w-5 h-5" />
                  Start First Question
                </Button>
                {!state.participants?.length && (
                  <p className="text-xs text-center text-muted-foreground">Waiting for at least one player to join</p>
                )}
              </div>
            </div>
          )}

          {/* Question active */}
          {state.status === "question_active" && (
            <div className="space-y-5" data-testid="live-host-question-view">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="text-sm px-3 py-1">
                    Question {state.current_index + 1} of {state.total_questions}
                  </Badge>
                  <QuestionCountdown remaining={state.question_time_remaining} limit={state.question_time_limit} testId="live-host-countdown" />
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Badge className="bg-green-500 gap-1">
                    <CheckCircle2 className="w-3 h-3" />
                    {state.participants.filter((p) => p.status === "correct").length} solved
                  </Badge>
                  <Badge variant="outline" className="gap-1">
                    <Copy className="w-3 h-3" /> Code: {state.join_code}
                  </Badge>
                </div>
              </div>

              {isNavQ ? (
                <div className="space-y-4" data-testid="live-host-nav-view">
                  <div className="bg-card rounded-2xl border border-border p-5" data-testid="live-host-question-card">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <h2 className="font-heading font-bold text-xl sm:text-2xl" data-testid="live-host-question-title">{state.question?.title || "Question unavailable"}</h2>
                        <p className="text-sm text-muted-foreground mt-1">Players program the drill on their own screens. Build along with the class here and press Play to show a run on the big screen.</p>
                      </div>
                      <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={toggleSolution} data-testid="live-host-solution-toggle">
                        {showSolution ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        {showSolution ? "Hide" : "Show"} Solution
                      </Button>
                    </div>
                    {showSolution && (
                      <div className="mt-4 border-2 border-amber-300 dark:border-amber-700 rounded-xl overflow-hidden" data-testid="live-host-sample-solution">
                        <div className="px-3 py-1.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 text-xs font-semibold uppercase tracking-wider">Sample Solution</div>
                        <div className="p-3">
                          {navSolution ? (
                            <Suspense fallback={<div className="p-3 text-sm text-muted-foreground">Loading the drill…</div>}>
                              <NavReplay grid={navSolution.nav_grid} run={navSolution.nav_run} workspace={blocksSolution?.workspace} python={blocksSolution?.python || sampleSolution} autoPlay testId="live-host-nav-solution" />
                            </Suspense>
                          ) : (
                            <p className="text-sm text-muted-foreground">No verified block solution saved for this grid yet.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  {state.question && (
                    <Suspense fallback={<div className="h-[420px] rounded-xl border border-border flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading the drill…</div>}>
                      <div data-testid="live-host-demo-nav">
                        <NavWorkspace
                          key={state.question.question_id}
                          question={state.question}
                          storageId={`live_demo_${session.session_id}_${state.question.question_id}`}
                          editorProps={{ hideSubmit: true }}
                        />
                      </div>
                    </Suspense>
                  )}
                </div>
              ) : (
              <div className="grid lg:grid-cols-2 gap-5 items-start">
                {/* Full question */}
                <div className="bg-card rounded-2xl border border-border p-6" data-testid="live-host-question-card">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h2 className="font-heading font-bold text-xl sm:text-2xl" data-testid="live-host-question-title">
                      {state.question?.title || "Question unavailable"}
                    </h2>
                    <Button variant="outline" size="sm" className="gap-1.5 shrink-0" onClick={toggleSolution} data-testid="live-host-solution-toggle">
                      {showSolution ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      {showSolution ? "Hide" : "Show"} Solution
                    </Button>
                  </div>
                  {state.question && (
                    <>
                      <p className="text-foreground leading-relaxed whitespace-pre-wrap mb-4">{state.question.description}</p>
                      {state.question.input_format && (
                        <div className="mb-3">
                          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Input Format</h3>
                          <p className="text-sm whitespace-pre-wrap">{state.question.input_format}</p>
                        </div>
                      )}
                      {state.question.output_format && (
                        <div className="mb-3">
                          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Output Format</h3>
                          <p className="text-sm whitespace-pre-wrap">{state.question.output_format}</p>
                        </div>
                      )}
                      <div className="grid sm:grid-cols-2 gap-4 mt-3">
                        {state.question.example_input && (
                          <div>
                            <span className="text-xs text-muted-foreground block mb-1">Example Input:</span>
                            <div className="io-block">{state.question.example_input}</div>
                          </div>
                        )}
                        <div>
                          <span className="text-xs text-muted-foreground block mb-1">Example Output:</span>
                          <div className="io-block">{state.question.example_output}</div>
                        </div>
                      </div>
                      {showSolution && (
                        <div className="mt-4 border-2 border-amber-300 dark:border-amber-700 rounded-xl overflow-hidden" data-testid="live-host-sample-solution">
                          <div className="px-3 py-1.5 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300 text-xs font-semibold uppercase tracking-wider">
                            Sample Solution
                          </div>
                          {blocksSolution ? (
                            <Suspense fallback={<div className="p-3 text-sm text-muted-foreground">Loading blocks…</div>}>
                              <BlocksSolutionView workspace={blocksSolution.workspace} inputs={blocksSolution.inputs || []} python={blocksSolution.python || sampleSolution} testId="live-host-blocks-solution" />
                            </Suspense>
                          ) : (
                            <pre className="p-3 text-sm font-mono whitespace-pre-wrap bg-card">
                              {sampleSolution || "No sample solution available for this question."}
                            </pre>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Teacher demo editor */}
                <div className="bg-card rounded-2xl border border-border p-6 space-y-3" data-testid="live-host-demo-panel">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Code2 className="w-4 h-4 text-primary" /> Demonstrate Live
                  </h3>
                  {isBlocksQ ? (
                    <Suspense fallback={<div className="h-[360px] rounded-xl border border-border flex items-center justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading blocks…</div>}>
                      <div data-testid="live-host-demo-blocks">
                        <BlocksWorkspace
                          key={state.question.question_id}
                          questionId={state.question.question_id}
                          storageId={`live_demo_${session.session_id}_${state.question.question_id}`}
                          inputs={state.question.blocks?.inputs || []}
                          rules={state.question.blocks || null}
                          starterWorkspace={state.question.blocks?.starter_workspace || null}
                          height={360}
                          onChange={(py) => setDemoCode(py)}
                        />
                      </div>
                    </Suspense>
                  ) : (
                    <div className="code-editor-wrapper" data-testid="live-host-demo-editor">
                      <CodeMirror
                        value={demoCode}
                        height="220px"
                        extensions={[python()]}
                        theme={isDarkMode ? oneDark : undefined}
                        onChange={(v) => setDemoCode(v)}
                        className="text-sm"
                      />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Terminal className="w-4 h-4" /> Custom Input
                    </label>
                    <Textarea
                      value={demoInput}
                      onChange={(e) => setDemoInput(e.target.value)}
                      className="font-mono text-sm resize-none h-16"
                      data-testid="live-host-demo-input"
                    />
                  </div>
                  <Button variant="outline" className="w-full gap-2 rounded-xl" onClick={handleDemoRun} disabled={demoRunning} data-testid="live-host-demo-run-btn">
                    {demoRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                    Run Code
                  </Button>
                  {demoOutput && (
                    <div className="io-block" data-testid="live-host-demo-output">
                      <pre className="whitespace-pre-wrap break-words overflow-auto">{demoOutput}</pre>
                    </div>
                  )}
                </div>
              </div>
              )}

              <ParticipantGrid />
              <Button
                className="w-full h-12 rounded-xl gap-2"
                onClick={() => sendAction(isLastQuestion ? "next_question" : "show_leaderboard")}
                data-testid="live-host-next-btn"
              >
                {isLastQuestion
                  ? <><Trophy className="w-4 h-4" /> Finish & Final Results</>
                  : <><ArrowRight className="w-4 h-4" /> Next — Show Leaderboard</>}
              </Button>
            </div>
          )}

          {/* Leaderboard */}
          {state.status === "leaderboard" && (
            <div className="space-y-5" data-testid="live-host-leaderboard-view">
              <LiveLeaderboard
                entries={state.leaderboard}
                title={`Leaderboard — after Question ${state.current_index + 1}`}
                resetKey={`${state.session_id}-${state.current_index}`}
                testId="live-host-leaderboard"
              />
              {state.leaderboard_solution && (
                <div className="bg-card rounded-2xl border border-border p-5" data-testid="live-host-leaderboard-solution">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Code2 className="w-4 h-4 text-primary" />
                    {state.leaderboard_solution.source === "player"
                      ? <>Correct solution by <span className="text-primary">{state.leaderboard_solution.nickname}</span></>
                      : "Sample Solution"}
                  </h3>
                  {state.leaderboard_solution.nav_grid ? (
                    <Suspense fallback={<div className="p-3 text-sm text-muted-foreground">Loading the drill…</div>}>
                      <NavReplay grid={state.leaderboard_solution.nav_grid} run={state.leaderboard_solution.nav_run} workspace={state.leaderboard_solution.workspace} python={state.leaderboard_solution.code} autoPlay testId="live-host-leaderboard-nav" />
                    </Suspense>
                  ) : isBlockMode(state.leaderboard_solution.mode) && state.leaderboard_solution.workspace ? (
                    <Suspense fallback={<div className="p-3 text-sm text-muted-foreground">Loading blocks…</div>}>
                      <BlocksSolutionView workspace={state.leaderboard_solution.workspace} inputs={state.leaderboard_solution.inputs || []} python={state.leaderboard_solution.code} testId="live-host-leaderboard-blocks" />
                    </Suspense>
                  ) : (
                    <pre className="io-block whitespace-pre-wrap">{state.leaderboard_solution.code}</pre>
                  )}
                </div>
              )}
              <Button className="w-full h-12 rounded-xl gap-2" onClick={() => sendAction("next_question")} data-testid="live-host-continue-btn">
                {isLastQuestion ? <><Trophy className="w-4 h-4" /> Finish & Final Results</> : <><ArrowRight className="w-4 h-4" /> Next Question ({state.current_index + 2} of {state.total_questions})</>}
              </Button>
            </div>
          )}

          {/* Finished */}
          {state.status === "finished" && (
            <div className="space-y-5 max-w-2xl mx-auto" data-testid="live-host-finished-view">
              <div className="text-center py-2">
                <Trophy className="w-12 h-12 text-amber-500 mx-auto mb-2" />
                <h2 className="font-heading font-bold text-2xl">All Questions Complete!</h2>
              </div>
              <LivePodium entries={state.leaderboard} />
              <Button
                className="w-full h-12 rounded-xl gap-2"
                onClick={() => { fetchDrills(); setContinueTrack(null); setContinueOpen(true); }}
                data-testid="live-host-continue-drill-btn"
              >
                <MonitorPlay className="w-4 h-4" /> Continue with Another Drill
              </Button>
              <Button variant="destructive" className="w-full h-12 rounded-xl gap-2" onClick={endSession} data-testid="live-host-finish-end-btn">
                <StopCircle className="w-4 h-4" /> End Session
              </Button>
            </div>
          )}
        </>
      )}

      {/* Continue with another drill dialog - grouped by track, session's track first */}
      <Dialog open={continueOpen} onOpenChange={setContinueOpen}>
        <DialogContent className="max-w-lg" data-testid="live-host-continue-dialog">
          <DialogHeader>
            <DialogTitle>Continue with Another Drill</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">
            Players stay in the session and keep their scores. Questions already played are skipped.
          </p>
          {(() => {
            const sessionTrack = normalizeMode(state?.mode || session?.mode);
            const activeTrack = continueTrack || sessionTrack;
            const played = new Set(state?.played_question_ids || []);
            const withPlayed = drills.map((d) => ({ ...d, allPlayed: (d.question_ids || []).length > 0 && (d.question_ids || []).every((q) => played.has(q)) }));
            const list = withPlayed
              .filter((d) => normalizeMode(d.mode) === activeTrack)
              .sort((a, b) => (a.allPlayed - b.allPlayed) || (a.difficulty || 0) - (b.difficulty || 0) || (a.title || "").localeCompare(b.title || ""));
            const counts = Object.fromEntries(MODES.map((m) => [m, withPlayed.filter((d) => normalizeMode(d.mode) === m && !d.allPlayed).length]));
            return (
              <>
                <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Drill track" data-testid="live-host-continue-track-filter">
                  {MODES.map((m) => {
                    const Icon = MODE_META[m].icon;
                    const on = activeTrack === m;
                    return (
                      <button key={m} type="button" role="tab" aria-selected={on} onClick={() => setContinueTrack(m)}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${on ? `${MODE_META[m].pill} border-transparent shadow` : "border-border text-muted-foreground hover:text-foreground"}`}
                        data-testid={`live-host-continue-track-${m}`}>
                        <Icon className="w-3.5 h-3.5" /> {MODE_META[m].label}
                        <span className={`rounded-full px-1.5 text-[10px] ${on ? "bg-white/25" : "bg-muted"}`}>{counts[m]}</span>
                      </button>
                    );
                  })}
                </div>
          {loadingDrills ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {counts[activeTrack] === 0 && (
                <div className="text-sm text-muted-foreground text-center py-4 space-y-1" data-testid="live-host-continue-empty">
                  <p>No {MODE_META[activeTrack].label} drills left to continue with{list.length ? " - the only ones have already been played in this session" : ""}.</p>
                  <p className="text-xs">Pick another track above, or ask your CodingDrills admin to add more {MODE_META[activeTrack].label} drills (Admin → Drills → Auto-build).</p>
                </div>
              )}
              {list.map((drill) => (
                <button
                  key={drill.drill_id}
                  onClick={() => continueWithDrill(drill.drill_id)}
                  disabled={continuing || drill.allPlayed}
                  title={drill.allPlayed ? "Every question in this drill has already been played in this session" : undefined}
                  className="w-full text-left p-4 rounded-xl border border-border bg-card hover:border-primary/50 transition-colors disabled:opacity-50 disabled:hover:border-border flex items-center justify-between gap-3"
                  data-testid={`live-host-continue-drill-${drill.drill_id}`}
                  data-all-played={drill.allPlayed ? "true" : undefined}
                >
                  <div>
                    <p className="font-semibold flex items-center gap-2 flex-wrap">{drill.title}<TrackBadge mode={drill.mode} />{drill.allPlayed && <Badge variant="secondary" className="text-[10px]">Already played</Badge>}</p>
                    <p className="text-xs text-muted-foreground">{drill.question_count} questions</p>
                    {drill.ungradable_count > 0 && (
                      <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1 mt-1" data-testid={`live-host-continue-drill-warning-${drill.drill_id}`}>
                        <AlertTriangle className="w-3 h-3 shrink-0" /> {drill.ungradable_count} question{drill.ungradable_count > 1 ? "s" : ""} without test data
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0">
                    {[...Array(drill.difficulty || 0)].map((_, i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                </button>
              ))}
            </div>
          )}
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
